import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { IntermediateElement } from "../../../../../http/vertigrip/types/intermediateElements";
import { useIntermediateElementsApi } from "../../../../../http/vertigrip/useIntermediateElements";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateIntermediateElementModalDialogButton from "./CreateIntermediateElementModalDialogButton";
import EditIntermediateElementModalDialog from "./EditIntermediateElementModalDialog";

type Props = {
  active?: boolean;
};

const SteelVersionIntermediateElements: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { steelVersionId } = useParams<{ steelVersionId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "steelVersionIntermediateElements" });
  const aborter = useRef(new AbortController());
  const intermediateElementsApi = useIntermediateElementsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "steelVersionIntermediateElements");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await intermediateElementsApi.updateAdminIntermediateElementVisibility(id, { visibility }, { steelVersionId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (intermediateElementId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await intermediateElementsApi.updateAdminIntermediateElementOrder(intermediateElementId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [idItemToEdit, setIdItemToEdit] = useState<string>();
  const [itemToDelete, setItemToDelete] = useState<IntermediateElement>();
  const { accessesAction, accessesDialog } = useEntityAccesses<IntermediateElement>("vertigrip.intermediate_element");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = intermediateElementsApi.useSteelVersionAdminIntermediateElements(
    steelVersionId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.intermediateElements.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.intermediateElements.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateIntermediateElementModalDialogButton steelVersionId={steelVersionId} />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<IntermediateElement>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.systemTypes.singular}</th>
            <th align="left">{i18n.translation.installationTypes.singular}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.id}`}>
                {item.code}
              </Link>
            </td>
            <td>{item.description}</td>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/vertigrip_system_types/${item.systemType?.id}`}
              >
                {item.systemType?.name}
              </Link>
            </td>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/installation_types/${item.installationType?.id}`}
              >
                {item.installationType?.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setIdItemToEdit(item.id)}
        rowActions={(item) => [
          getEditAction(i18n, () => setIdItemToEdit(item.id)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.intermediateElements.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                const deleteElement = (res: Promise<void>) =>
                  res.then(() => setItemToDelete(undefined)).catch((err) => dispatch(showErrorNotification(i18n, err)));

                if (itemToDelete.installationType) {
                  deleteElement(
                    intermediateElementsApi.deleteAdminIntermediateElement(
                      itemToDelete.id,
                      itemToDelete.vertigripSteelVersion.id,
                      itemToDelete.installationType.id,
                      null,
                    ),
                  );
                } else if (itemToDelete.systemType) {
                  deleteElement(
                    intermediateElementsApi.deleteAdminIntermediateElement(
                      itemToDelete.id,
                      itemToDelete.vertigripSteelVersion.id,
                      null,
                      itemToDelete.systemType.id,
                    ),
                  );
                }
                setIsSubmitting(false);
              },
            },
          ]}
        >
          {i18n.translation.intermediateElements.questions.delete}
        </ModalDialog>
      )}
      <EditIntermediateElementModalDialog
        onClose={() => setIdItemToEdit(undefined)}
        steelVersionId={steelVersionId}
        intermediateElementId={idItemToEdit}
      />
    </FlexContent>
  );
};

export default SteelVersionIntermediateElements;
